import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlagIcon } from '@/components/layout/FlagIcon';
import { cn } from '@/lib/utils';
import { t, getLanguage, setLanguage, LANGUAGES } from '@/i18n';

const flagClass = 'h-3 w-[18px] shrink-0 rounded-[2px] ring-1 ring-black/10';

const choose = (code) => code !== getLanguage() && setLanguage(code);

// All UI languages side by side, so it is obvious the app speaks several.
// The mobile header has no room for four flags, so it keeps a flag menu.
export const LanguageSwitcher = () => {
  const current = getLanguage();

  return (
    <>
      <div
        role="group"
        aria-label={t('header.language')}
        className="hidden sm:flex items-center gap-0.5 rounded-lg bg-muted/70 p-0.5"
      >
        {LANGUAGES.map(lang => {
          const active = lang.code === current;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => choose(lang.code)}
              aria-label={lang.label}
              aria-pressed={active}
              title={lang.label}
              className={cn(
                'flex items-center gap-1.5 h-7 rounded-md px-1.5 md:px-2 text-xs font-semibold uppercase transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'bg-white text-foreground shadow-sm'
                  : 'text-muted-foreground opacity-70 hover:opacity-100 hover:bg-white/60',
              )}
            >
              <FlagIcon code={lang.code} className={flagClass} />
              <span className="hidden md:inline">{lang.code}</span>
            </button>
          );
        })}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="sm:hidden gap-1 h-8 px-1.5" aria-label={t('header.language')}>
            <FlagIcon code={current} className={flagClass} />
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            {t('header.language')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {LANGUAGES.map(lang => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => choose(lang.code)}
              className={cn('gap-2', current === lang.code && 'bg-muted/50 font-medium')}
            >
              <FlagIcon code={lang.code} className={flagClass} />
              {lang.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
